import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Cascader, Dialog, Input, ZhMap, type CascaderOption, type ZhMapRef } from "zhihao-ui-react";
import type { MapViewForm } from "@zhihaoscm/entity";
import { FormTypeEnum } from "@zhihaoscm/meta";
import { areaLib } from "@/utils/areaLib";
import { CDN_URL } from "@/config";
import { onAddressInput, sliceGbCode } from "@/views/pages/logistics/wharf";

// 与旧版 zhMap 及 zhihao-ui-react 的地图默认中心保持一致，避免空经纬度导致地图初始化异常。
const DEFAULT_MAP_CENTER: [number, number] = [114.84, 30.52];
const DEFAULT_MAP_LNG_LAT = DEFAULT_MAP_CENTER.map(value => value.toFixed(6)).join(",");

/** 将经纬度文本解析为地图中心，非法值统一回退到缺省中心。 */
function parseMapCenter(value?: string): [number, number] | undefined {
    const parts = value?.split(",").map(item => item.trim());
    if (!parts || parts.length < 2 || parts.slice(0, 2).some(item => !item)) return undefined;
    const point = parts.map(item => Number(item));
    if (point.some(item => !Number.isFinite(item))) return undefined;
    return [point[0], point[1]];
}

export interface ZhMapDialogProps {
    formType: FormTypeEnum;
    pointerMarker: boolean;
    isShowAddress?: boolean;
    visible?: boolean;
    data: MapViewForm;
    onUpdateVisible?: (value: boolean) => void;
    onUpdateData?: (value: MapViewForm) => void;
    onSubmit?: (data: MapViewForm, formType: FormTypeEnum) => void;
}

/** 位置选择弹窗：使用 zhihao-ui-react 的 Dialog、Cascader 和 ZhMap。 */
export default function ZhMapDialog({
    formType,
    pointerMarker,
    isShowAddress = false,
    visible = false,
    data,
    onUpdateVisible,
    onUpdateData,
    onSubmit
}: ZhMapDialogProps) {
    const mapRef = useRef<ZhMapRef | null>(null);
    // 地图 moveend 回调需要保持稳定，避免 zhihao-ui-react 因回调变化销毁并重建地图实例。
    const dataRef = useRef(data);
    dataRef.current = data;
    const [keyword, setKeyword] = useState("");
    const [suggestions, setSuggestions] = useState<
        Array<{ value: string; link?: { lonlat?: string; countyCode?: string; address?: string } }>
    >([]);
    const options = useMemo<CascaderOption[]>(
        () => areaLib.areaList2Options(areaLib.areaData) as CascaderOption[],
        [areaLib.version]
    );
    const areaCode = data.areaCode || [];
    const parsedMapCenter = useMemo(() => parseMapCenter(data.lngLat), [data.lngLat]);
    const mapCenter = parsedMapCenter || DEFAULT_MAP_CENTER;
    const displayLngLat = data.lngLat && parsedMapCenter ? data.lngLat : DEFAULT_MAP_LNG_LAT;
    const title = formType === FormTypeEnum.Scan ? "查看位置" : "位置";
    const patchData = (patch: Partial<MapViewForm>) => onUpdateData?.({ ...data, ...patch });
    useEffect(() => {
        if (!visible) return;
        // 首次打开新增弹窗时补齐坐标，保持旧版弹窗可直接确认并提交缺省位置。
        if (!parseMapCenter(data.lngLat)) onUpdateData?.({ ...data, lngLat: DEFAULT_MAP_LNG_LAT });
        mapRef.current?.setCenter(mapCenter[0], mapCenter[1]);
    }, [visible, data, data.lngLat, mapCenter, onUpdateData]);
    useEffect(() => {
        if (!visible || typeof window === "undefined") return;
        // Dialog 过渡期间容器尺寸可能为 0，地图需在弹窗完成布局后重新计算尺寸才能挂载瓦片图层。
        let secondFrame: number | undefined;
        let refreshTimer: number | undefined;
        const refreshMap = () => {
            const map = mapRef.current?.getInstall();
            map?.updateSize();
            // updateSize 只更新视口尺寸，显式同步一次渲染确保过渡期间错过首帧的瓦片立即绘制。
            map?.renderSync();
        };
        const frame = window.requestAnimationFrame(() => {
            secondFrame = window.requestAnimationFrame(() => {
                refreshMap();
                // Dialog 内容通过 portal 延迟挂载时，补一次延迟刷新，确保 ref 已连接到地图实例。
                refreshTimer = window.setTimeout(refreshMap, 300);
            });
        });
        return () => {
            window.cancelAnimationFrame(frame);
            if (secondFrame !== undefined) window.cancelAnimationFrame(secondFrame);
            if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
        };
    }, [visible]);
    const searchAddress = async (value: string) => {
        setKeyword(value);
        if (!value.trim()) {
            setSuggestions([]);
            return;
        }
        setSuggestions(((await onAddressInput(value, areaCode[2] || "")) as typeof suggestions) || []);
    };
    const selectAddress = (item: (typeof suggestions)[number]) => {
        const lngLat = item.link?.lonlat || "";
        const nextAreaCode = item.link?.countyCode ? sliceGbCode(item.link.countyCode) : areaCode;
        patchData({
            address: item.link?.address || item.value,
            detailAddress: item.link?.address || item.value,
            lngLat,
            areaCode: nextAreaCode
        });
        const [lon, lat] = lngLat.split(",").map(Number);
        if (Number.isFinite(lon) && Number.isFinite(lat)) mapRef.current?.setCenter(lon, lat);
        setSuggestions([]);
    };
    const updateCenter = useCallback(() => {
        if (isShowAddress) return;
        const center = mapRef.current?.getCenter();
        if (!center) return;
        const lngLat = `${center[0].toFixed(6)},${center[1].toFixed(6)}`;
        if (dataRef.current.lngLat === lngLat) return;
        onUpdateData?.({ ...dataRef.current, lngLat });
    }, [isShowAddress, onUpdateData]);
    return (
        <Dialog
            title={title}
            className="zh-map-dialog"
            open={visible}
            width="920px"
            closeOnClickModal={false}
            destroyOnClose
            onCancel={() => onUpdateVisible?.(false)}
            footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button onClick={() => onUpdateVisible?.(false)}>取消</Button>
                    {formType !== FormTypeEnum.Scan && (
                        <Button type="primary" onClick={() => onSubmit?.(data, formType)}>
                            确定
                        </Button>
                    )}
                </div>
            }
        >
            {formType !== FormTypeEnum.Scan && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 10, marginBottom: 10 }}>
                    <Cascader
                        options={options}
                        value={areaCode}
                        filterable
                        placeholder="请选择地区"
                        onChange={value => patchData({ areaCode: value as string[] })}
                    />
                    <div style={{ position: "relative" }}>
                        <Input
                            value={keyword || data.detailAddress || data.address || ""}
                            maxLength={200}
                            placeholder="请输入详细地址"
                            onChange={event => searchAddress(event.target.value)}
                        />
                        {suggestions.length > 0 && (
                            <div
                                style={{
                                    position: "absolute",
                                    zIndex: 5,
                                    width: "100%",
                                    background: "#fff",
                                    border: "1px solid #ddd"
                                }}
                            >
                                {suggestions.map((item, index) => (
                                    <button
                                        key={`${item.value}-${index}`}
                                        type="button"
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            padding: 8,
                                            textAlign: "left",
                                            border: 0,
                                            background: "#fff"
                                        }}
                                        onClick={() => selectAddress(item)}
                                    >
                                        {item.value} {item.link?.address}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <span style={{ lineHeight: "32px" }}>经纬度 {displayLngLat}</span>
                </div>
            )}
            {/* 旧版 zh-map-dialog 的 body 固定 482px，扣除上下内边距后地图视口为 434px。 */}
            <div style={{ position: "relative", height: 434 }}>
                {visible ? (
                    <ZhMap
                        ref={mapRef}
                        center={mapCenter}
                        className="h-full w-full"
                        layerType="satellite"
                        copyrightImgUrl={`${CDN_URL}map/tdt-copyright-h-w.svg`}
                        mapMoveEnd={updateCenter}
                    />
                ) : null}
                {pointerMarker && !isShowAddress && (
                    <div
                        style={{
                            pointerEvents: "none",
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <img src={`${CDN_URL}map/poi-marker-default.png`} alt="pointer-marker" style={{ height: 50 }} />
                        <span style={{ padding: "1px 6px", background: "#ffffffe6", borderRadius: 4 }}>
                            {displayLngLat}
                        </span>
                    </div>
                )}
            </div>
        </Dialog>
    );
}
